import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

export type Theme =
  | "light"
  | "dark"
  | "system"
  | "dark-midnight"
  | "dark-graphite"
  | "dark-abyss"
  | "dark-forest"
  | "dark-dusk"
  | "dark-expanse"
  | "light-nova"
  | "light-ivory"
  | "light-blush"
  | "light-sage"
  | "light-sand"
  | "light-mist";

export type AccentColor =
  | "blue"
  | "green"
  | "violet"
  | "orange"
  | "rose"
  | "amber"
  | "teal"
  | "slate"
  | "crimson"
  | "gold"
  | "indigo"
  | "jade";

export type Density = "compact" | "comfortable" | "spacious";
export type FontSize = "sm" | "md" | "lg";
export type SidebarStyle = "inset" | "floating" | "minimal" | "bold" | "neon" | "mac";
export type FontFamily =
  | "system"
  | "inter"
  | "ubuntu"
  | "geist"
  | "jetbrains-mono"
  | "plus-jakarta"
  | "dm-sans"
  | "nunito"
  | "outfit";

export type LockScreenStyle =
  | "minimal"
  | "gradient"
  | "aurora"
  | "editorial"
  | "glass"
  | "neon"
  | "terminal"
  | "paper";

export type LoginScreenStyle =
  | "classic"
  | "centered"
  | "magazine"
  | "immersive"
  | "minimal"
  | "spotlight"
  | "card"
  | "orbital";

// Session / idle timer preferences
export interface SessionPrefs {
  /** How many seconds of inactivity before the badge flips to "Idle" (default 5) */
  idleBadgeSeconds: number;
  /** How many minutes of inactivity before auto-logout (default 5) */
  logoutMinutes: number;
  /** How many seconds before logout to show the warning countdown (default 10) */
  warningSeconds: number;
  /** Whether the idle status badge is shown at all */
  showIdleBadge: boolean;
  /** Badge display style: icon + text label, or icon only */
  badgeStyle: "icon-text" | "icon-only";
}

// Notification preferences
export interface NotificationPrefs {
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  // per-category toggles
  feeAlerts: boolean;
  attendanceAlerts: boolean;
  examAlerts: boolean;
  announcementAlerts: boolean;
  disciplineAlerts: boolean;
  leaveAlerts: boolean;
  systemAlerts: boolean;
  // digest
  digestEnabled: boolean;
  digestFrequency: "daily" | "weekly" | "never";
}

// Accessibility / display
export interface AccessibilityPrefs {
  reduceMotion: boolean;
  highContrast: boolean;
  focusIndicators: boolean;
  fontSize: FontSize;
  lineHeight: "normal" | "relaxed" | "loose";
}

interface UIState {
  // Appearance
  theme: Theme;
  accentColor: AccentColor;
  radius: number;
  density: Density;
  sidebarStyle: SidebarStyle;
  fontSize: FontSize;
  fontFamily: FontFamily;

  // Layout
  sidebarCollapsed: boolean;

  // Locale
  locale: string;

  // Screens
  lockScreenStyle: LockScreenStyle;
  loginScreenStyle: LoginScreenStyle;

  // Session
  session: SessionPrefs;

  // Notifications
  notifications: NotificationPrefs;

  // Accessibility
  accessibility: AccessibilityPrefs;

  // Actions
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setAccentColor: (color: AccentColor) => void;
  setRadius: (radius: number) => void;
  setDensity: (density: Density) => void;
  setSidebarStyle: (style: SidebarStyle) => void;
  setFontSize: (size: FontSize) => void;
  setFontFamily: (font: FontFamily) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setLocale: (locale: string) => void;
  setLockScreenStyle: (style: LockScreenStyle) => void;
  setLoginScreenStyle: (style: LoginScreenStyle) => void;
  setSessionPref: <K extends keyof SessionPrefs>(key: K, value: SessionPrefs[K]) => void;
  setNotificationPref: <K extends keyof NotificationPrefs>(key: K, value: NotificationPrefs[K]) => void;
  setAccessibilityPref: <K extends keyof AccessibilityPrefs>(key: K, value: AccessibilityPrefs[K]) => void;
}

const DEFAULT_SESSION: SessionPrefs = {
  idleBadgeSeconds: 5,
  logoutMinutes: 5,
  warningSeconds: 10,
  showIdleBadge: true,
  badgeStyle: "icon-text",
};

const DEFAULT_NOTIFICATIONS: NotificationPrefs = {
  emailEnabled: true,
  smsEnabled: false,
  pushEnabled: true,
  feeAlerts: true,
  attendanceAlerts: true,
  examAlerts: true,
  announcementAlerts: true,
  disciplineAlerts: true,
  leaveAlerts: true,
  systemAlerts: true,
  digestEnabled: false,
  digestFrequency: "daily",
};

const DEFAULT_ACCESSIBILITY: AccessibilityPrefs = {
  reduceMotion: false,
  highContrast: false,
  focusIndicators: true,
  fontSize: "md",
  lineHeight: "normal",
};

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set, get) => ({
        theme: "dark-midnight",
        accentColor: "blue",
        radius: 0.625,
        density: "comfortable",
        sidebarStyle: "inset",
        fontSize: "md",
        fontFamily: "system" as FontFamily,
        sidebarCollapsed: false,
        locale: "en",
        lockScreenStyle: "aurora",
        loginScreenStyle: "classic",
        session: DEFAULT_SESSION,
        notifications: DEFAULT_NOTIFICATIONS,
        accessibility: DEFAULT_ACCESSIBILITY,

        setTheme: (theme) => set({ theme }),
        toggleTheme: () => {
          const current = get().theme;
          const next =
            current === "light"
              ? "dark"
              : current === "dark"
                ? "system"
                : current === "system"
                  ? "light"
                  : "light";
          set({ theme: next });
        },
        setAccentColor: (accentColor) => set({ accentColor }),
        setRadius: (radius) => set({ radius }),
        setDensity: (density) => set({ density }),
        setSidebarStyle: (sidebarStyle) => set({ sidebarStyle }),
        setFontSize: (fontSize) => set({ fontSize }),
        setFontFamily: (fontFamily) => set({ fontFamily }),
        setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
        toggleSidebar: () =>
          set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
        setLocale: (locale) => set({ locale }),
        setLockScreenStyle: (lockScreenStyle) => set({ lockScreenStyle }),
        setLoginScreenStyle: (loginScreenStyle) => set({ loginScreenStyle }),
        setSessionPref: (key, value) =>
          set((state) => ({ session: { ...state.session, [key]: value } })),
        setNotificationPref: (key, value) =>
          set((state) => ({ notifications: { ...state.notifications, [key]: value } })),
        setAccessibilityPref: (key, value) =>
          set((state) => ({ accessibility: { ...state.accessibility, [key]: value } })),
      }),
      { name: "skul-ui" },
    ),
    { name: "ui" },
  ),
);
